import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Watch, Bell, DoorOpen, Smartphone, Heart, Zap, Home, ShieldCheck, Fingerprint, Clock, CheckCircle, AlertTriangle } from "lucide-react";

export default function PrivateClientsSection() {
  const { t } = useTranslation();

  const systemComponents = [
    { icon: Watch, title: t("private.comp1Title"), description: t("private.comp1Desc") },
    { icon: Bell, title: t("private.comp2Title"), description: t("private.comp2Desc") },
    { icon: DoorOpen, title: t("private.comp3Title"), description: t("private.comp3Desc") },
    { icon: Smartphone, title: t("private.comp4Title"), description: t("private.comp4Desc") }
  ];

  const benefits = [
    { icon: Heart, title: t("private.ben1Title"), description: t("private.ben1Desc") },
    { icon: Zap, title: t("private.ben2Title"), description: t("private.ben2Desc") },
    { icon: Home, title: t("private.ben3Title"), description: t("private.ben3Desc") },
    { icon: Fingerprint, title: t("private.ben4Title"), description: t("private.ben4Desc") },
    { icon: ShieldCheck, title: t("private.ben5Title"), description: t("private.ben5Desc") },
    { icon: Clock, title: t("private.ben6Title"), description: t("private.ben6Desc") }
  ];

  const scrollToContact = () => {
    const element = document.querySelector("#kontakti");
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <section id="privatpersonam" className="section-spacing bg-white" data-testid="private-clients-section">
      <div className="section-container">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {t("private.badge")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("private.title")}
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            {t("private.description")}
          </p>
        </div>

        {/* System Components */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-foreground mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("private.systemTitle")}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {systemComponents.map((comp, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border hover:shadow-lg transition-shadow duration-300 ${index === 0 ? 'bg-primary text-white border-primary' : 'bg-white border-border'}`}
                data-testid={`private-component-${index + 1}`}
              >
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${index === 0 ? 'bg-white/20' : 'bg-primary/10'}`}>
                  <comp.icon className={`w-7 h-7 ${index === 0 ? 'text-white' : 'text-primary'}`} />
                </div>
                <h4 className={`text-lg font-bold mb-2 ${index === 0 ? 'text-white' : 'text-foreground'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {comp.title}
                </h4>
                <p className={index === 0 ? 'text-white/90' : 'text-muted-foreground'}>
                  {comp.description}
                </p>
              </div>
            ))}
          </div>

          {/* Pilot project note */}
          <div className="mt-8 bg-primary/5 rounded-2xl p-8 text-center" data-testid="private-pilot-note">
            <p className="text-lg text-foreground font-medium">
              {t("private.pilotNote")}
            </p>
          </div>

          {/* Safety blocks */}
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="rounded-2xl overflow-hidden border border-border" data-testid="private-safety-home">
              <div className="bg-primary p-6">
                <h3 className="text-xl font-bold text-white text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t("private.safetyHome")}
                </h3>
              </div>
              <div className="bg-white p-6">
                <p className="text-muted-foreground leading-relaxed">
                  {t("private.safetyHomeDesc")}
                </p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-border" data-testid="private-safety-mobile">
              <div className="bg-primary p-6">
                <h3 className="text-xl font-bold text-white text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {t("private.safetyMobile")}
                </h3>
              </div>
              <div className="bg-white p-6">
                <p className="text-muted-foreground leading-relaxed">
                  {t("private.safetyMobileDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What the system solves */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-border mt-16 mb-16" data-testid="private-system-solves">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t("private.solvesTitle")}
              </h3>
            </div>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              {t("private.solvesDesc1")}
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("private.solvesDesc2")}
            </p>
            <h4 className="text-xl font-bold text-foreground mt-8 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("private.solvesSubtitle")}
            </h4>

            {/* Sensor examples */}
            <div className="space-y-4">
              {t("private.sensorExamples", { returnObjects: true }).map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-secondary/50 rounded-xl" data-testid={`sensor-example-${index + 1}`}>
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-white">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-bold text-foreground mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{item.device}</p>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quote */}
        <div className="mt-12 mb-16 bg-primary/5 rounded-2xl p-8 text-center" data-testid="private-quote">
          <p className="text-lg md:text-xl text-foreground font-medium italic leading-relaxed max-w-3xl mx-auto">
            <span className="text-primary font-bold not-italic">„</span>{t("private.quote")}<span className="text-primary font-bold not-italic">"</span>
          </p>
        </div>

        {/* Benefits */}
        <div className="bg-secondary rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("private.benefitsTitle")}
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("private.benefitsDesc")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300" data-testid={`private-benefit-${index + 1}`}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {benefit.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-10 bg-primary rounded-2xl p-8 text-center text-white">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8" />
              <h4 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t("private.highlightTitle")}
              </h4>
            </div>
            <p className="text-white/90 max-w-2xl mx-auto mb-6">
              {t("private.highlightDesc")}
            </p>
            <Button
              onClick={scrollToContact}
              className="bg-white text-primary hover:bg-white/90 h-14 px-8 rounded-full font-semibold text-lg"
              data-testid="private-cta"
            >
              {t("private.highlightCta")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
